import { createFormHook } from "@tanstack/react-form";
import { DisplayField } from "./fields/display-field";
import { fieldContext, formContext } from "./form-context";
import { SubmitButton } from "./submit-button";
import { applyValidationPolicy } from "./validation-policy";

const { useAppForm: _createAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    DisplayField,
  },
  formComponents: {
    SubmitButton,
  },
});

/**
 * Wraps the base form hook to enforce the shared validation policy: the schema
 * you pass as `onChange` (or `onBlur`) runs on change, blur and submit. See
 * validation-policy.ts. Field components never re-validate by hand.
 *
 * TRAP — `form.reset(x)` where `x` is not deep-equal to the `defaultValues` you
 * pass here is silently undone one render later. react-form re-applies
 * `update(opts)` in a layout effect on EVERY render (no dep array), and
 * form-core's `update` overwrites `values` with `defaultValues` whenever it is
 * deep-unequal to the stored defaults AND no field is touched — which is exactly
 * the state `reset()` leaves behind (it clears every field's `isTouched`). So a
 * reset-to-empty on a form whose `defaultValues` carry loaded data snaps the
 * values back, and any state derived from them (a disabled send button) flips
 * with them. Either reset to the same values you pass as `defaultValues`, or
 * hold `defaultValues` in a ref you advance alongside the reset — see
 * `useChatComposerForm`'s `defaultValuesRef` in the webapp.
 */
export const useAppForm: typeof _createAppForm = (options) => {
  const { validators, ...rest } = options as any;
  return _createAppForm({
    ...rest,
    validators: applyValidationPolicy(validators),
  } as any);
};
